import Api, { apiError, requestSuccess } from "@Api";
import { INCOMING_CALL, UPDATE_CALL, CLEAR_CALL, CALL_COMMAND_START, CALL_COMMAND_END, SET_CALL_MUTE } from "@Actions/types";
import { toNumber, get } from "lodash";

const TERMINAL_STATUSES = new Set(["ended", "rejected", "missed"]);

const normalizeStatus = (status = "") => {
	const normalized = String(status).toLowerCase();
	if (["incoming", "ringing", "queued", "initiated", "pre_accept"].includes(normalized)) return "incoming";
	if (["ongoing", "in-progress", "in_progress", "connected", "callconnected", "accepted", "connecting"].includes(normalized))
		return "ongoing";
	if (["rejected", "declined"].includes(normalized)) return "rejected";
	if (["missed", "no-answer", "busy"].includes(normalized)) return "missed";
	if (["ended", "completed", "cancelled", "canceled", "failed", "hangup"].includes(normalized)) return "ended";
	return normalized;
};

const normalizeCallPayload = (data = {}, fallbackStatus = "ringing") => {
	const callData = get(data, "call", data);
	const callId = String(
		get(callData, "callId", get(callData, "call_id", get(callData, "callSid", get(data, "callId", get(data, "callSid", "")))))
	);
	const rawStatus = get(callData, "status", get(data, "status", fallbackStatus));
	const status = normalizeStatus(rawStatus === "unknown" || !rawStatus ? fallbackStatus : rawStatus);
	const duration = toNumber(get(callData, "duration", get(data, "duration", 0)));
	const from = get(callData, "from", get(data, "from", get(data, "contact.phone", "")));
	const callerName = get(callData, "callerName", get(data, "callerName", get(data, "contact.name", "")));
	const direction = get(callData, "direction", get(data, "direction", ""));
	const startedAt = get(callData, "startedAt", get(data, "startedAt", null));
	const endedAt = get(callData, "endedAt", get(data, "endedAt", null));
	const connectedAt = get(callData, "connectedAt", get(data, "connectedAt", null));
	const channelId = get(callData, "channelId", get(data, "channelId", get(data, "chat.channelId", get(data, "message.channelId", null))));
	const session = get(callData, "session", get(data, "session", null));
	const callMode = get(callData, "callMode", get(data, "callMode", get(session, "mode", "p2p")));
	const micMuted = Boolean(get(callData, "micMuted", get(data, "micMuted", false)));

	return {
		callSid: callId,
		status,
		duration,
		from,
		callerName,
		direction,
		startedAt,
		connectedAt,
		endedAt,
		channelId,
		session,
		callMode,
		micMuted,
		raw: data,
		updatedAt: Date.now(),
	};
};

const isDifferentActiveCall = (activeCall, incomingCallSid) => {
	if (!activeCall) return false;
	if (TERMINAL_STATUSES.has(activeCall.status)) return false;
	return activeCall.callSid && incomingCallSid && activeCall.callSid !== incomingCallSid;
};

export function callIncoming(data) {
	return (dispatch, getState) => {
		const payload = normalizeCallPayload(data, "incoming");
		const { calls } = getState();
		if (isDifferentActiveCall(calls.activeCall, payload.callSid)) return;
		dispatch(requestSuccess(INCOMING_CALL, { ...payload, status: "incoming" }));
	};
}

export function callTaken({ callSid }) {
	return (dispatch, getState) => {
		const { calls } = getState();
		if (!calls.activeCall) return;
		if (String(calls.activeCall.callSid) !== String(callSid)) return;
		if (calls.activeCall.status !== "incoming") return;
		dispatch(requestSuccess(CLEAR_CALL));
	};
}

export function callUpdated(data) {
	return (dispatch, getState) => {
		const { calls } = getState();
		const payload = normalizeCallPayload(data, calls.activeCall ? "ongoing" : "incoming");
		if (!calls.activeCall) {
			dispatch(requestSuccess(INCOMING_CALL, { ...payload, status: "incoming" }));
		} else {
			if (isDifferentActiveCall(calls.activeCall, payload.callSid)) return;
			dispatch(requestSuccess(UPDATE_CALL, payload));
		}
	};
}

export function callCleared() {
	return (dispatch) => {
		dispatch(requestSuccess(CLEAR_CALL));
	};
}

export function acceptIncomingCall({ callSid, channelId, session, emitCommand }) {
	return async (dispatch) => {
		dispatch(requestSuccess(CALL_COMMAND_START, "accept"));
		try {
			const normalizedCallSid = String(callSid || "");
			const commandPayload = {
				callSid: normalizedCallSid,
				channelId: channelId || undefined,
				session: session || undefined,
			};
			if (emitCommand) {
				await emitCommand("callAccept", commandPayload);
			} else {
				await Api.post("/calls/acceptCall", commandPayload);
			}
			dispatch(requestSuccess(UPDATE_CALL, { status: "ongoing", updatedAt: Date.now() }));
			return true;
		} catch (error) {
			dispatch(apiError(error?.message || "No se pudo aceptar la llamada"));
			return false;
		} finally {
			dispatch(requestSuccess(CALL_COMMAND_END));
		}
	};
}

export function rejectIncomingCall({ callSid, channelId, emitCommand }) {
	return async (dispatch) => {
		dispatch(requestSuccess(CALL_COMMAND_START, "reject"));
		try {
			const normalizedCallSid = String(callSid || "");
			const commandPayload = {
				callSid: normalizedCallSid,
				channelId: channelId || undefined,
			};
			if (emitCommand) {
				await emitCommand("callReject", commandPayload);
			} else {
				await Api.post("/calls/rejectCall", commandPayload);
			}
			dispatch(requestSuccess(UPDATE_CALL, { status: "rejected", updatedAt: Date.now() }));
		} catch (error) {
			dispatch(apiError(error?.message || "No se pudo rechazar la llamada"));
		} finally {
			dispatch(requestSuccess(CALL_COMMAND_END));
		}
	};
}

export function setCallMuteState(micMuted) {
	return (dispatch) => {
		dispatch(requestSuccess(SET_CALL_MUTE, Boolean(micMuted)));
	};
}

export function hangupIncomingCall({ callSid, channelId, emitCommand }) {
	return async (dispatch) => {
		dispatch(requestSuccess(CALL_COMMAND_START, "terminate"));
		try {
			const normalizedCallSid = String(callSid || "");
			const commandPayload = {
				callSid: normalizedCallSid,
				channelId: channelId || undefined,
			};
			if (emitCommand) {
				await emitCommand("callTerminate", commandPayload);
			} else {
				await Api.post("/calls/terminateCall", commandPayload);
			}
			dispatch(requestSuccess(UPDATE_CALL, { status: "ended", endedAt: Date.now(), updatedAt: Date.now() }));
			// Let the widget's auto-clear timeout handle CLEAR_CALL so the terminal state
			// renders first — this ensures WebRTC cleanup runs before activeCall is nulled.
		} catch (error) {
			dispatch(apiError(error?.message || "No se pudo finalizar la llamada"));
		} finally {
			dispatch(requestSuccess(CALL_COMMAND_END));
		}
	};
}

export function transferIncomingCall({ callSid, userId = null, groupId = null }) {
	return async (dispatch) => {
		dispatch(requestSuccess(CALL_COMMAND_START, "transfer"));
		try {
			await Api.post("/calls/transferCall", { callSid, userId, groupId });
			dispatch(requestSuccess(CLEAR_CALL));
			return true;
		} catch (error) {
			dispatch(apiError(error?.message || "No se pudo transferir la llamada"));
			return false;
		} finally {
			dispatch(requestSuccess(CALL_COMMAND_END));
		}
	};
}
